import type { Dispatch } from '@reduxjs/toolkit';

import { signUpStepsActions } from 'state/signUpSteps/signUpStepsSlice';
import type {
  StepComponentProps,
  Step,
  StepsContainer,
} from 'components/Stepper';
import { SignUpSteps } from 'enums/signUp';

import CreateAccountComponent, {
  isCreateAccountFormValues,
} from '../CreateAccount';
import VerifyAccountComponent from '../VerifyAccount';
import VerifyingAccountComponent from '../VerifyingAccount';
import SetupPasswordComponent, {
  isSetupPasswordFormValues,
} from '../SetupPassword';
import AccountDetailsComponent, {
  isAccountDetailsFormValues,
} from '../AccountDetails';
import RedirectingComponent from '../Redirecting';

const {
  CREATE_ACCOUNT_REQUEST,
  SETUP_PASSWORD_REQUEST,
  ACCOUNT_DETAILS_REQUEST,
} = signUpStepsActions;

const getSignUpSteps = (
  dispatch?: Dispatch
): StepsContainer<StepComponentProps> => {
  const createAccount: Step<StepComponentProps> = {
    index: SignUpSteps.CreateAccount,
    label: 'Create account',
    formId: 'create-account',
    onNext: (values) => {
      if (dispatch && isCreateAccountFormValues(values)) {
        dispatch(
          CREATE_ACCOUNT_REQUEST({
            username: values.email,
            acceptTermsAndConditions: values.acceptTermsAndConditions,
          })
        );
      }
    },
    component: CreateAccountComponent,
    get stepLabelProps() {
      return {
        StepIconProps: {
          icon: this.index + 1,
        },
      };
    },
  };

  const verifyAccount: Step<StepComponentProps> = {
    index: SignUpSteps.VerifyAccount,
    label: 'Verify account',
    formId: 'verify-account',
    component: VerifyAccountComponent,
    get stepLabelProps() {
      return {
        StepIconProps: {
          icon: this.index + 1,
        },
      };
    },
    get stepProps() {
      /*
       * custom logic when active step = VerifyingAccount (hidden step)
       * to show VerifyAccount as active until VerifyingAccount API call
       * completes successfully
       */
      return (activeStep: number) => ({
        ...(activeStep === this.index + 1 && {
          active: true,
          completed: false,
        }),
      });
    },
  };

  const verifyingAccount: Step<StepComponentProps> = {
    index: SignUpSteps.VerifyingAccount,
    label: 'Verifying account',
    hidden: true,
    component: VerifyingAccountComponent,
  };

  const setupPassword: Step<StepComponentProps> = {
    index: SignUpSteps.SetupPassword,
    label: 'Setup password',
    formId: 'setup-password',
    onNext: (values) => {
      if (dispatch && isSetupPasswordFormValues(values)) {
        dispatch(
          SETUP_PASSWORD_REQUEST({
            password: values.password,
            username: values.username,
          })
        );
      }
    },
    component: SetupPasswordComponent,
    get stepLabelProps() {
      return {
        StepIconProps: {
          icon: this.index,
        },
      };
    },
  };

  const accountDetails: Step<StepComponentProps> = {
    index: SignUpSteps.AccountDetails,
    label: 'Account details',
    formId: 'account-details',
    onNext: (values) => {
      if (dispatch && isAccountDetailsFormValues(values)) {
        const { username, firstName, lastName, phoneNumber, country } = values;
        dispatch(
          ACCOUNT_DETAILS_REQUEST({
            username,
            firstName,
            lastName,
            phoneNumber,
            country,
          })
        );
      }
    },
    component: AccountDetailsComponent,
    get stepLabelProps() {
      return {
        StepIconProps: {
          icon: this.index,
        },
      };
    },
  };

  const accountCreated: Step<StepComponentProps> = {
    index: SignUpSteps.AccountCreated,
    label: 'Account created',
    hidden: true,
    component: RedirectingComponent,
  };

  const steps = [
    createAccount,
    verifyAccount,
    verifyingAccount,
    setupPassword,
    accountDetails,
    accountCreated,
  ];

  return {
    steps,
    length: steps.length,
    displayLength: steps.filter((step) => !step.hidden).length,
  };
};

export default getSignUpSteps;
